import type { Fixture } from '../types';

/**
 * 用法线在图像平面上的投影决定椭圆主轴方向；斜墙（法线水平分量大）拉长椭圆，
 * 使蒙版几何与表面朝向一致，避免仅靠扇形「猜」洗墙范围。
 * @returns 是否已绘制（false 时回退到扇形蒙版）
 */
export function tryDrawSpatialBeamMask(
  ctx: CanvasRenderingContext2D,
  fixture: Fixture,
  imgWidth: number,
  imgHeight: number,
  diagonal: number,
  shortEdge: number,
): boolean {
  const n = fixture.spatialNormal;
  if (!n || typeof n.nx !== 'number' || typeof n.ny !== 'number' || typeof n.nz !== 'number') {
    return false;
  }
  const { nx, ny, nz } = n;

  let normalizedX = fixture.x;
  let normalizedY = fixture.y;
  if (fixture.x > 1000 || fixture.y > 1000) {
    normalizedX = (fixture.x / imgWidth) * 1000;
    normalizedY = (fixture.y / imgHeight) * 1000;
  }
  normalizedX = Math.max(0, Math.min(1000, normalizedX));
  normalizedY = Math.max(0, Math.min(1000, normalizedY));

  const x = (normalizedX / 1000) * imgWidth;
  const y = (normalizedY / 1000) * imgHeight;

  const isSpotlight = fixture.type === 'spotlight';
  const horiz = Math.sqrt(nx * nx + nz * nz);
  const wallLike = Math.abs(ny) < 0.62;
  /** 图像平面内椭圆旋转：法线在 XZ 上的投影（相机朝 -Z 近似） */
  const rotationDeg = (Math.atan2(-nx, nz) * 180) / Math.PI;
  const elong = wallLike ? 1.38 + 1.15 * Math.min(1.25, horiz) : 1.08 + 0.4 * Math.min(1, horiz);
  const baseR = Math.min(
    diagonal * (isSpotlight ? 0.28 : 0.24),
    shortEdge * (isSpotlight ? 0.58 : 0.52),
  );
  const rx = baseR * elong;
  const ry = baseR / Math.sqrt(Math.max(1.01, elong));

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.scale(Math.max(rx, 8), Math.max(ry, 8));
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.88)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  return true;
}
